import type { NonUndefined } from 'utility-types'

import type { ReadonlyRecord } from '../../../../../../utils'

import { areCybusServiceEquals, Capability, type CybusService, type PaginatedData } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SubscriptionFilterArgs, SubscriptionPageOptions } from '../../../../../../application'

import type { BackendJsonResponseContent, BackendQueryParameters } from '../../../../../Connectware'
import { mapSearch } from '../../../../mappers'
import { createSortMapper, mapPagination, mapPaginationQueryParameters } from '../../../mappers'
import { BaseSubscriptionStrategy } from '../Base'
import { mapBaseService } from './mappers'

type IndexedLinks = ReadonlyRecord<CybusService['id'], CybusService['links']>

type ServicesOrderByFields = NonUndefined<BackendQueryParameters<'/api/v2/services', 'GET'>['orderBy']>

const mapSort = createSortMapper<PageSubscriptionsTypes['services'], ServicesOrderByFields>({
    deviation: null,
    id: 'serviceId',
    name: 'name',
    status: 'currentState',
    version: 'version',
})

export class ServicesStrategy extends BaseSubscriptionStrategy<'services', never> {
    protected override readonly areEquals = areCybusServiceEquals

    override readonly requiredPermissions = [Capability.SERVICES_READ]

    override readonly supportsPagination = true

    override async retrievePage ({
        pagination,
        search,
        sort,
    }: SubscriptionFilterArgs & SubscriptionPageOptions<'services'>): Promise<PaginatedData<PageSubscriptionsTypes['services']>> {
        const queryParams = {
            ...mapPaginationQueryParameters(pagination),
            ...mapSort(sort),
            ...mapSearch(search),
        } as const

        const [linksByService, { data, meta }] = await Promise.all([
            this.request<'/api/services/all-links', 'GET', IndexedLinks>({
                capability: Capability.SERVICES_READ,
                method: 'GET',
                path: '/api/services/all-links',
                queryParams: { onlyFromEnabledServices: 'true' },
                authenticate: true,
                handlers: {
                    200: (response) =>
                        response
                            .getJson()
                            .then((data) => data.reduce<IndexedLinks>((r, linkedService) => ({ ...r, [linkedService.id]: [...linkedService.links] }), {})),
                },
            }),
            this.request<'/api/v2/services', 'GET', BackendJsonResponseContent<'/api/v2/services', 'GET', 200>>({
                path: '/api/v2/services',
                method: 'GET',
                capability: Capability.SERVICES_READ,
                authenticate: true,
                queryParams,
                handlers: {
                    200: ServicesStrategy.createJsonHandler(),
                    500: ServicesStrategy.createServerErrorHandler('Could not fetch services'),
                },
            }),
        ])

        return {
            current: await Promise.all(
                data.map(async (service) => ({
                    ...mapBaseService(service, Boolean(await this.rstAdapter.fetchServiceDeviationsCount('services', service.serviceId))),
                    links: linksByService[service.serviceId] ?? [],
                }))
            ),
            ...mapPagination(meta.pagination),
        }
    }

    override async retrieveCount (): Promise<number> {
        return this.request<'/api/v2/services/stats/count', 'GET', number>({
            path: '/api/v2/services/stats/count',
            method: 'GET',
            capability: Capability.SERVICES_READ,
            authenticate: true,
            handlers: {
                200: (response) => response.getJson().then((j) => j.data.servicesCount),
                500: ServicesStrategy.createServerErrorHandler('Could not fetch services count'),
            },
        })
    }

    createAllChangeListener (_: SubscriptionFilterArgs, handler: VoidFunction): Promise<VoidFunction> {
        /**
         * Deviation changes should be detected so the state is as up to date as it can
         */
        return this.rstAdapter.subscribeToServicesDeviationsCount('services', handler)
    }
}
